import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  purchasesUrl, purchaseByIdUrl, purchaseApproveUrl, purchaseApproveAllUrl,
  purchaseReceiveAllUrl, purchaseApproveReceiveAllUrl,
  purchaseRejectUrl, purchaseReceiveUrl,
  suppliersUrl, productsUrl, branchesUrl, facilityUrl, ASSETS,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtMoney, fmtDate, fmtNumber, titleCase } from '../../utils/format';
import { toastSuccess, showAxiosError, swalConfirm, swalDangerConfirm, swalWarning } from '../../utils/swal';
import { defaultImageFor } from '../../utils/productImage';

const STATUS_BADGE = {
  pending: 'badge-gold',
  approved: 'badge-blue',
  received: 'badge-green',
  rejected: 'badge-red',
};

// Inline timeline — supplier → branch chain of custody
const PurchaseTimeline = ({ p }) => {
  if (p.status === 'rejected') {
    return (
      <div className="tl">
        <div className="tl-row done"><div className="tl-dot">✓</div><div className="tl-bar" /><div className="tl-text">
          <div className="tl-label">Ordered</div>
          <div className="tl-meta">{p.ordered_by_name || '—'} · {fmtDate(p.ordered_at)}</div>
        </div></div>
        <div className="tl-row done danger"><div className="tl-dot">✕</div><div className="tl-text">
          <div className="tl-label">Rejected</div>
          <div className="tl-meta">PO closed — no stock impact</div>
        </div></div>
      </div>
    );
  }
  const steps = [
    { label: 'Ordered',  who: p.ordered_by_name,  at: p.ordered_at,  done: true },
    { label: 'Approved', who: p.approved_by_name, at: p.approved_at, done: !!p.approved_at },
    { label: 'Received', who: p.received_by_name, at: p.received_at, done: !!p.received_at, tone: 'ok' },
  ];
  return (
    <div className="tl">
      {steps.map((s, i) => (
        <div key={s.label} className={`tl-row ${s.done ? 'done' : ''} ${s.tone || ''}`}>
          <div className="tl-dot">{s.done ? '✓' : i + 1}</div>
          {i < steps.length - 1 && <div className="tl-bar" />}
          <div className="tl-text">
            <div className="tl-label">{s.label}</div>
            <div className="tl-meta">
              {s.done ? <>{s.who || '—'} · {fmtDate(s.at)}</> : <span style={{ color: 'var(--mute-2)' }}>pending</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Purchases = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper = me?.role === 'super_admin';
  const isManager = me?.role === 'branch_manager' || isSuper;
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ branch_id: me?.branch_id || '', supplier_id: '', note: '' });
  const [lines, setLines] = useState([{ product_id: '', qty: '', unit_cost: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [view, setView] = useState(null); // { purchase, items }
  const [filter, setFilter] = useState({ status: '', search: '' });
  const [purchaseMode, setPurchaseMode] = useState('both');
  const [productSearch, setProductSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, pr, b, f] = await Promise.all([
        axios.get(purchasesUrl), axios.get(suppliersUrl), axios.get(productsUrl),
        // Branches are needed for both super_admin (picker) and the mode filter.
        axios.get(branchesUrl).catch(() => ({ data: { results: [] } })),
        axios.get(facilityUrl).catch(() => ({ data: { facility: null } })),
      ]);
      setRows(p.data?.results || []);
      setSuppliers(s.data?.results || []);
      setProducts(pr.data?.results || []);
      setBranches(b.data?.results || []);
      setPurchaseMode(f.data?.facility?.purchase_mode || 'both');
    } finally { setLoading(false); }
  };

  // Branches the current user can actually raise a PO for, given the facility's purchase_mode.
  const eligibleBranches = useMemo(() => {
    const scopedBranches = isSuper ? branches : branches.filter((b) => b.id === me?.branch_id);
    if (purchaseMode === 'hq_only') return scopedBranches.filter((b) => b.is_head_office);
    if (purchaseMode === 'branch_direct') return scopedBranches.filter((b) => !b.is_head_office);
    return scopedBranches;
  }, [branches, isSuper, me, purchaseMode]);
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, []);

  const productMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const total = useMemo(() =>
    lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0)
  , [lines]);

  const filteredRows = useMemo(() => {
    const q = filter.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter.status && r.status !== filter.status) return false;
      if (!q) return true;
      return (r.purchase_no || '').toLowerCase().includes(q)
          || (r.supplier_name || '').toLowerCase().includes(q)
          || (r.branch_name || '').toLowerCase().includes(q)
          || (r.ordered_by_name || '').toLowerCase().includes(q);
    });
  }, [rows, filter]);

  const stats = useMemo(() => {
    let pending = 0, approved = 0, received = 0, totalOpen = 0;
    for (const r of rows) {
      if (r.status === 'pending') { pending += 1; totalOpen += Number(r.total_amount) || 0; }
      else if (r.status === 'approved') { approved += 1; totalOpen += Number(r.total_amount) || 0; }
      else if (r.status === 'received') received += 1;
    }
    return { pending, approved, received, totalOpen };
  }, [rows]);

  const openCreate = () => {
    // Mode gating: HQ-only blocks non-HQ users; branch_direct blocks HQ users.
    if (eligibleBranches.length === 0) {
      if (purchaseMode === 'hq_only') {
        return swalWarning(
          'Purchases are HQ-only',
          'This facility is set to route every purchase through the head office. Ask the HQ team to raise the PO and transfer the stock to your branch.',
        );
      }
      if (purchaseMode === 'branch_direct') {
        return swalWarning(
          'HQ does not raise purchases',
          'This facility is set to branch-direct purchasing. Each branch raises its own POs.',
        );
      }
    }
    const firstBranch = eligibleBranches[0]?.id || '';
    setForm({ branch_id: me?.branch_id || firstBranch, supplier_id: '', note: '' });
    setLines([{ product_id: '', qty: '', unit_cost: '' }]);
    setProductSearch('');
    setOpen(true); setError(null);
  };

  // Add a product to the current order, or increment qty if it's already a line.
  const addProductToOrder = (p) => {
    setLines((prev) => {
      const idx = prev.findIndex((l) => Number(l.product_id) === p.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: String((Number(next[idx].qty) || 0) + 1) };
        return next;
      }
      // Drop the empty placeholder row if it's still untouched
      const cleaned = prev.filter((l) => l.product_id);
      return [...cleaned, { product_id: String(p.id), qty: '1', unit_cost: String(p.cost_price || 0) }];
    });
  };

  const productOptions = useMemo(() => {
    if (!productSearch.trim()) return products.slice(0, 36);
    const q = productSearch.trim().toLowerCase();
    return products.filter((p) =>
      [p.name, p.sku, p.barcode, p.category_name].some((v) => String(v || '').toLowerCase().includes(q))
    ).slice(0, 36);
  }, [products, productSearch]);

  const submit = async () => {
    setError(null);
    if (!form.branch_id) { setError('Choose a branch'); return; }
    const items = lines
      .filter((l) => l.product_id && Number(l.qty) > 0 && Number(l.unit_cost) >= 0)
      .map((l) => ({ product_id: Number(l.product_id), qty: Number(l.qty), unit_cost: Number(l.unit_cost) }));
    if (items.length === 0) { setError('Add at least one line'); return; }
    setSaving(true);
    try {
      const res = await axios.post(purchasesUrl, { branch_id: Number(form.branch_id), supplier_id: form.supplier_id || null, note: form.note || null, items });
      if (res.data?.success) {
        setOpen(false); await load();
        toastSuccess(`Purchase ${res.data.purchaseNo} recorded — awaiting approval`);
      } else setError(res.data?.message || 'Failed');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const openView = async (id) => {
    const res = await axios.get(purchaseByIdUrl(id));
    if (res.data?.success) setView({ purchase: res.data.purchase, items: res.data.items });
  };

  const doAction = async (url, okMsg, confirmTitle, confirmText, danger = false) => {
    if (confirmTitle) {
      const confirmed = danger
        ? await swalDangerConfirm(confirmTitle, confirmText)
        : await swalConfirm(confirmTitle, confirmText);
      if (!confirmed) return;
    }
    try {
      await axios.patch(url);
      toastSuccess(okMsg);
      setView(null);
      await load();
    } catch (err) {
      showAxiosError(err);
    }
  };

  const doApproveAll = async () => {
    const confirmed = await swalConfirm(
      `Approve all ${stats.pending} pending purchase${stats.pending === 1 ? '' : 's'}?`,
      'Every pending purchase order will be marked approved and become eligible to receive. Individual rejections can still be done from each order.',
    );
    if (!confirmed) return;
    try {
      const res = await axios.patch(purchaseApproveAllUrl);
      toastSuccess(res.data?.message || 'All pending purchases approved');
      await load();
    } catch (err) {
      showAxiosError(err);
    }
  };

  const doReceiveAll = async () => {
    const confirmed = await swalConfirm(
      `Receive all ${stats.approved} approved purchase${stats.approved === 1 ? '' : 's'}?`,
      'Stock on every approved PO will be added to the branch inventory in one atomic operation. Product cost prices are also updated to the last-received cost.',
      { confirmButtonText: 'Yes, receive all' },
    );
    if (!confirmed) return;
    try {
      const res = await axios.patch(purchaseReceiveAllUrl);
      toastSuccess(res.data?.message || 'All approved purchases received');
      await load();
    } catch (err) {
      showAxiosError(err);
    }
  };

  const doApproveAndReceiveAll = async () => {
    const total = stats.pending + stats.approved;
    const confirmed = await swalConfirm(
      `Approve & receive ${total} purchase${total === 1 ? '' : 's'} at once?`,
      `${stats.pending} pending PO${stats.pending === 1 ? '' : 's'} will be approved AND ${stats.pending + stats.approved} approved PO${(stats.pending + stats.approved) === 1 ? '' : 's'} received. Inventory updates in one atomic transaction. Use this only when you trust every PO in the queue.`,
      { confirmButtonText: 'Yes, clear the queue' },
    );
    if (!confirmed) return;
    try {
      const res = await axios.patch(purchaseApproveReceiveAllUrl);
      toastSuccess(res.data?.message || 'Queue cleared');
      await load();
    } catch (err) {
      showAxiosError(err);
    }
  };

  return (
    <>
      {/* KPI strip */}
      <div className="kpi-row">
        <KpiStat label="Pending"      val={stats.pending}     hint="Awaiting approval" tone="warn"    icon="⏳" />
        <KpiStat label="Approved"     val={stats.approved}    hint="Ready to receive"  tone="brand"   icon="✓" />
        <KpiStat label="Received"     val={stats.received}    hint="Stock added"       tone="success" icon="📥" />
        <KpiStat label="Open value"   val={fmtMoney(stats.totalOpen)} hint="Pending + approved" tone="accent" icon="₵" raw />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2>Purchases</h2>
            <span className="sub">
              Record stock that a supplier delivers <strong>directly to a branch</strong>. Approve, then receive to bring it onto branch shelves.
            </span>
          </div>
          <div className="head-actions">
            {isManager && stats.pending > 0 && stats.approved > 0 && (
              <button
                className="btn-success"
                onClick={doApproveAndReceiveAll}
                title="Approve every pending PO AND receive every approved PO in one go"
              >
                ⚡ Approve &amp; receive all ({stats.pending + stats.approved})
              </button>
            )}
            {isManager && stats.pending > 0 && (
              <button className="btn-success" onClick={doApproveAll} title="Approve all pending purchases at once">
                ✓ Approve all ({stats.pending})
              </button>
            )}
            {isManager && stats.approved > 0 && (
              <button className="btn-success" onClick={doReceiveAll} title="Receive all approved purchases and update stock">
                📥 Receive all ({stats.approved})
              </button>
            )}
            {isManager && <button className="btn-primary" onClick={openCreate}>＋ New purchase</button>}
          </div>
        </div>

        <div className="toolbar">
          <div className="search-field" style={{ maxWidth: 320 }}>
            <span className="ic">🔍</span>
            <input
              className="f-input"
              placeholder="Search PO #, supplier, branch, person"
              value={filter.search}
              onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            />
            {filter.search && <button className="clear" onClick={() => setFilter((f) => ({ ...f, search: '' }))} aria-label="Clear">×</button>}
          </div>
          <select className="f-select" style={{ maxWidth: 200 }} value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="received">Received</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {loading ? (
          <div className="empty-state"><div className="ic">⏳</div><h4>Loading purchases…</h4></div>
        ) : filteredRows.length === 0 ? (
          <div className="empty-state">
            <div className="ic">📥</div>
            <h4>No purchases yet</h4>
            <p>Record a purchase when a supplier delivers stock to one of your branches. Inventory updates only after the goods are received.</p>
            {isManager && <button className="btn-primary" onClick={openCreate}>＋ New purchase</button>}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Date</th>
                  {isSuper && <th>Branch</th>}
                  <th>Supplier</th>
                  <th>Ordered by</th>
                  <th className="right">Total</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((p) => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openView(p.id)}>
                    <td className="strong">{p.purchase_no}</td>
                    <td className="muted">{fmtDate(p.ordered_at)}</td>
                    {isSuper && <td>{p.branch_name}</td>}
                    <td>{p.supplier_name || <span className="muted">— Direct —</span>}</td>
                    <td>{p.ordered_by_name || '—'}</td>
                    <td className="right num strong">{fmtMoney(p.total_amount)}</td>
                    <td><span className={`badge ${STATUS_BADGE[p.status] || 'badge-gray'}`}>{titleCase(p.status)}</span></td>
                    <td className="right"><button className="btn-link" onClick={(e) => { e.stopPropagation(); openView(p.id); }}>Open →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE — two-pane: order details on the left, product picker on the right */}
      <Modal
        title="New purchase order" open={open} onClose={() => setOpen(false)} width={1180}
        footer={
          <>
            <div className="po-footer-total">
              <span className="lbl">Order total</span>
              <strong>{fmtMoney(total)}</strong>
              <span className="ct">{lines.filter((l) => l.product_id).length} line{lines.filter((l) => l.product_id).length === 1 ? '' : 's'}</span>
            </div>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving || lines.every((l) => !l.product_id)}>
              {saving ? 'Saving…' : 'Record purchase'}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}

        <div className="po-modal-layout">
          {/* LEFT — order header + line table */}
          <section className="po-modal-left">
            <h4 className="po-section-head">Order details</h4>
            <div className="f-grid f-grid-2">
              <div className="f-row">
                <label className="f-label">
                  Branch *
                  {purchaseMode !== 'both' && (
                    <span className="po-mode-tag">{purchaseMode === 'hq_only' ? 'HQ only' : 'Branch direct'}</span>
                  )}
                </label>
                <select
                  className="f-select"
                  value={form.branch_id}
                  onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                  disabled={eligibleBranches.length <= 1}
                >
                  {eligibleBranches.length === 0 && <option value="">— No eligible branch —</option>}
                  {eligibleBranches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}{b.is_head_office ? ' (HQ)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="f-row">
                <label className="f-label">Supplier</label>
                <select className="f-select" value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
                  <option value="">— Direct purchase —</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Note / reference</label>
                <input
                  className="f-input"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Invoice number, delivery note, supplier reference…"
                />
              </div>
            </div>

            <h4 className="po-section-head" style={{ marginTop: 20 }}>
              Line items
              <span className="po-lines-count">{lines.filter((l) => l.product_id).length}</span>
            </h4>
            <div className="po-lines">
              {lines.filter((l) => l.product_id).length === 0 ? (
                <div className="po-lines-empty">
                  <div className="ic">🛒</div>
                  <h4>No items yet</h4>
                  <p>Tap any product on the right to add it to this order. Tap again to increase quantity.</p>
                </div>
              ) : (
                lines.map((l, i) => {
                  if (!l.product_id) return null;
                  const p = productMap.get(Number(l.product_id));
                  if (!p) return null;
                  const lt = (Number(l.qty) || 0) * (Number(l.unit_cost) || 0);
                  return (
                    <div key={i} className="po-line">
                      <img
                        src={`${ASSETS}${p.image || defaultImageFor(p)}`}
                        alt=""
                        onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                      />
                      <div className="po-line-info">
                        <div className="nm">{p.name}</div>
                        <div className="meta"><code>{p.sku}</code> · {p.unit}</div>
                      </div>
                      <div className="po-line-fields">
                        <div className="po-field">
                          <label>Qty</label>
                          <input
                            type="number" step="0.001" min="0"
                            value={l.qty}
                            onChange={(e) => { const n = [...lines]; n[i].qty = e.target.value; setLines(n); }}
                          />
                        </div>
                        <div className="po-field">
                          <label>Unit cost (GHS)</label>
                          <input
                            type="number" step="0.01" min="0"
                            value={l.unit_cost}
                            onChange={(e) => { const n = [...lines]; n[i].unit_cost = e.target.value; setLines(n); }}
                          />
                        </div>
                        <div className="po-line-total">
                          <span className="lbl">Line</span>
                          <strong>{fmtMoney(lt)}</strong>
                        </div>
                        <button
                          className="po-line-remove"
                          onClick={() => setLines((prev) => prev.filter((_, x) => x !== i))}
                          title="Remove line"
                          aria-label="Remove"
                        >×</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* RIGHT — product picker grid */}
          <aside className="po-modal-right">
            <h4 className="po-section-head">Add products</h4>
            <div className="po-product-search">
              <span className="ic">🔍</span>
              <input
                className="f-input"
                placeholder="Search by name, SKU, barcode, category…"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                autoFocus
              />
              {productSearch && (
                <button className="clear" onClick={() => setProductSearch('')} title="Clear">×</button>
              )}
            </div>
            <div className="po-product-grid">
              {productOptions.length === 0 ? (
                <div className="po-empty-mini">
                  No products match "<strong>{productSearch}</strong>".
                </div>
              ) : productOptions.map((p) => {
                const inOrder = lines.find((l) => Number(l.product_id) === p.id);
                const qtyInOrder = inOrder ? Number(inOrder.qty || 0) : 0;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`po-product-card ${qtyInOrder > 0 ? 'selected' : ''}`}
                    onClick={() => addProductToOrder(p)}
                    title="Add to order"
                  >
                    <img
                      src={`${ASSETS}${p.image || defaultImageFor(p)}`}
                      alt=""
                      onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                    />
                    <div className="nm">{p.name}</div>
                    <div className="meta">
                      <code>{p.sku}</code>
                      <span className="cost">{fmtMoney(p.cost_price)}</span>
                    </div>
                    {qtyInOrder > 0 && <span className="qty-badge">{fmtNumber(qtyInOrder)}</span>}
                  </button>
                );
              })}
            </div>
            <p className="po-picker-hint">
              💡 Showing up to 36 results. Refine your search to narrow the grid.
            </p>
          </aside>
        </div>
      </Modal>

      {/* VIEW + ACTIONS */}
      <Modal
        title={view ? `Purchase ${view.purchase.purchase_no}` : 'Purchase'}
        open={!!view} onClose={() => setView(null)} width={720}
        footer={view && (
          <>
            <button className="btn-ghost" onClick={() => setView(null)}>Close</button>
            {view.purchase.status === 'pending' && isManager && (
              <>
                <button className="btn-danger" onClick={() => doAction(
                  purchaseRejectUrl(view.purchase.id),
                  'Purchase rejected',
                  'Reject this purchase?',
                  'The PO will be closed. No stock is moved. Use this for cancelled or erroneous orders.',
                  true,
                )}>Reject</button>
                <button className="btn-primary" onClick={() => doAction(
                  purchaseApproveUrl(view.purchase.id),
                  'Purchase approved',
                  'Approve this purchase?',
                  'It will become eligible to receive. Stock is added only once you mark it received.',
                )}>Approve</button>
              </>
            )}
            {view.purchase.status === 'approved' && (
              <button className="btn-primary" onClick={() => doAction(
                purchaseReceiveUrl(view.purchase.id),
                'Stock received — inventory updated',
                'Confirm goods received?',
                'Branch inventory will be increased by the qty on each line, and product cost prices updated.',
              )}>📥 Mark received</button>
            )}
          </>
        )}
      >
        {view && (
          <>
            <div className="field-row" style={{ marginBottom: 6 }}>
              <Detail label="Branch"  value={<span className="strong">{view.purchase.branch_name}</span>} />
              <Detail label="Supplier" value={view.purchase.supplier_name || <span className="muted">— Direct delivery —</span>} />
              <Detail label="Status"  value={<span className={`badge ${STATUS_BADGE[view.purchase.status] || 'badge-gray'}`}>{titleCase(view.purchase.status)}</span>} />
            </div>

            <PurchaseTimeline p={view.purchase} />

            <div className="divider-text">Items</div>

            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Product</th><th className="right">Qty</th><th className="right">Unit cost</th><th className="right">Line</th></tr></thead>
                <tbody>
                  {view.items.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <div className="strong">{it.product_name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{it.sku ? `SKU ${it.sku}` : ''}{it.unit ? ` · ${it.unit}` : ''}</div>
                      </td>
                      <td className="right num">{fmtNumber(it.qty)}</td>
                      <td className="right num">{fmtMoney(it.unit_cost)}</td>
                      <td className="right num strong">{fmtMoney(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="right strong">Total</td>
                    <td className="right num strong">{fmtMoney(view.purchase.total_amount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {view.purchase.note && (
              <div className="notice info" style={{ marginTop: 14 }}>
                <strong>Note:</strong>&nbsp;{view.purchase.note}
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

const KpiStat = ({ label, val, hint, tone, icon, raw }) => (
  <div className="stat">
    <div className="lbl">{label}</div>
    <div className="val">{raw ? val : fmtNumber(val)}</div>
    <div className="hint">{hint}</div>
    <div className="ic" style={
      tone === 'brand'   ? { background: 'var(--brand-soft)',   color: 'var(--brand)' }
    : tone === 'accent'  ? { background: 'var(--accent-soft)',  color: 'var(--accent)' }
    : tone === 'warn'    ? { background: 'var(--warn-soft)',    color: 'var(--warn)' }
    : tone === 'success' ? { background: 'var(--success-soft)', color: 'var(--success)' }
    : {}}>{icon}</div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="field" style={{ marginBottom: 0 }}>
    <label className="f-label up">{label}</label>
    <div>{value}</div>
  </div>
);

export default Purchases;
